import "./App.css";
import { Route, Routes, useLocation } from "react-router-dom";
import "typeface-nunito-sans";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { theme, ThemePaths } from "./theme";
import GlobalStyles from "@mui/material/GlobalStyles";
import { globalStyle } from "./GlobalStyle";
import Header from "./components/Header";
import { ToastContainer } from "react-toastify";
import AuthProvider from "./components/AuthProvider";
import "react-toastify/dist/ReactToastify.min.css";
import ProtectedRoute from "./components/ProtectedRoute";
import AddressAndAccountContextProvider from "./components/AddressAndAccountProvider/AddressAndAccountProvider";
import { library } from "@fortawesome/fontawesome-svg-core";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import {
  IconDefinition,
  IconPrefix,
  IconPack,
} from "@fortawesome/free-solid-svg-icons";
import { getThemePath } from "./hooks/useGetThemePath";
import SubscriberPortalConfigurationContextProvider from "./context/SubscriberPortalConfigurations";
import BillingProvider from "./components/BillingProvider";
import { useEffect, Suspense, lazy } from "react";
import { warmCommonDataCache } from "./utils/cacheWarming";
import { CircularProgress, Box } from "@mui/material";

// Lazy-loaded components for code splitting
const Login = lazy(() => import("./routes/Login"));
const ForgotPassword = lazy(() => import("./routes/ForgotPassword"));
const AccountSettings = lazy(() => import("./routes/AccountSettings"));
const Billing = lazy(() => import("./routes/Billing"));
const CreateAccount = lazy(
  () => import("./routes/CreateAccount/CreateAccount")
);
const ForgotEmail = lazy(() => import("./routes/ForgotEmail"));
const Dashboard = lazy(() => import("./routes/Dashboard"));
const Services = lazy(() => import("./routes/Services"));
const UserProfile = lazy(() => import("./routes/Profile/UserProfile"));
const HelpCenter = lazy(() => import("./routes/HelpCenter"));
const FaqPage = lazy(
  () => import("./routes/HelpCenter/components/Faq/FaqPage")
);
const DeviceGuidePage = lazy(
  () => import("./routes/HelpCenter/components/DeviceGuide/DeviceGuidePage")
);
const DeviceTroubleshooting = lazy(
  () =>
    import(
      "./routes/HelpCenter/components/DeviceGuide/DeviceTroubleshootingPage"
    )
);
const ServiceProvidersPage = lazy(
  () =>
    import(
      "./routes/HelpCenter/components/ServiceProviders/ServiceProvidersPage"
    )
);
const BillingIssuesPage = lazy(
  () => import("./routes/HelpCenter/components/BillingIssues/BillingIssuesPage")
);
const SecurityQuestionsPage = lazy(() => import("./routes/SecurityQuestions"));
const ConnectDevice = lazy(() => import("./routes/ConnectDevice"));
const UpdatePassword = lazy(() => import("./routes/UpdatePassword"));

type IconDefinitionOrPack = IconDefinition | IconPack;

interface ImportedIcons {
  [key: string]: IconPrefix | IconDefinitionOrPack;
}

const iconList = Object.keys(Icons)
  .filter((key) => key !== "fas" && key !== "prefix")
  .map((icon) => (Icons as ImportedIcons)[icon]);

library.add(...(iconList as IconDefinitionOrPack[]));

// Function to create MUI theme with the correct theme path
const getMaterialUITheme = (themePath: ThemePaths) => {
  // Default theme values for safety
  const defaultColors = {
    white: "#fff",
    table: {
      primary: "#6d6d6d",
      background: "#f0f0f0",
      border: "#c5c5c5",
      evenCell: "#FAFAFA",
    },
    menu: {
      item: {
        selectedBG: "#FAFAFA",
        hoverBG: "#FAFAFA",
        focusVisibleBG: "#FAFAFA",
      },
    },
    textPrimary: {
      heading: "#253331",
    },
  };

  // Safely access theme colors with fallbacks
  const colors = theme[themePath]?.colors || defaultColors;

  return createTheme({
    typography: {
      fontFamily: theme.fontFamily,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 845,
        lg: 1220,
        xl: 1400,
      },
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            fontFamily: "Inter",
            boxShadow: "none",
            borderBottom: "2px solid #C4C4C4",
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            fontFamily: "Inter",
            fontWeight: 700,
            fontSize: "16px",
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            fontFamily: "Inter",
            color: "#686868",
            fontSize: "16px",
            marginBottom: "16px",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: "Inter",
          },
          head: {
            color: colors.table.primary,
            fontWeight: "600",
            fontSize: "16px",
            backgroundColor: colors.table.background,
            borderBottom: `1px solid ${colors.table.border}`,
            "&:first-of-type": {
              borderTopLeftRadius: "10px",
            },
            "&:last-child": {
              borderTopRightRadius: "10px",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(odd)": {
              backgroundColor: colors.white,
            },
            "&:nth-of-type(even)": {
              backgroundColor: colors.table.evenCell,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "4px 8px",
            margin: "2px 8px",
            borderRadius: "8px",
            "&.Mui-selected": {
              backgroundColor: colors.menu.item.selectedBG,
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: colors.menu.item.hoverBG,
              },
            },
            "&.Mui-focusVisible": {
              backgroundColor: colors.menu.item.focusVisibleBG,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: colors.textPrimary.heading,
          },
        },
      },
    },
  });
};

// Loading component with centered spinner
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);

export const routes = {
  login: {
    name: "login",
    path: "/",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Login />
      </Suspense>
    ),
    hasHeader: false,
  },
  createAccount: {
    name: "create-account",
    path: "/create-account",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BillingProvider>
          <CreateAccount />
        </BillingProvider>
      </Suspense>
    ),
    hasHeader: false,
  },
  secuityQuestions: {
    name: "security-questions",
    path: "/security-questions",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <SecurityQuestionsPage />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  forgotPassword: {
    name: "forgot-password",
    path: "/forgot-password",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ForgotPassword />
      </Suspense>
    ),
    hasHeader: false,
  },
  forgotEmail: {
    name: "forgot-email",
    path: "/forgot-email",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ForgotEmail />
      </Suspense>
    ),
    hasHeader: false,
  },
  updateAccount: {
    name: "update-account",
    path: "/update-account",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <AccountSettings />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: true,
  },
  updatePassword: {
    name: "update-password",
    path: "/update-password",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <UpdatePassword />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  dashboard: {
    name: "dashboard",
    path: "/dashboard",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  billing: {
    name: "billing",
    path: "/billing",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <Billing />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  services: {
    name: "services",
    path: "/services",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <Services />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  myServices: {
    name: "my-services",
    path: "/services?tab=My%20Services",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <Services />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  profile: {
    name: "profile",
    path: "/profile",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <UserProfile />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  help: {
    name: "help",
    path: "/help",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <HelpCenter />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  helpService: {
    name: "help-service",
    path: "/help/service",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <ServiceProvidersPage />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  helpDevice: {
    name: "help-device",
    path: "/help/device",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <DeviceGuidePage />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  helpTroubleshoot: {
    name: "help-troubleshoot",
    path: "/help/troubleshoot",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <DeviceTroubleshooting />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  helpBilling: {
    name: "help-billing",
    path: "/help/billing",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <BillingIssuesPage />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  connectDevice: {
    name: "connect-device",
    path: "/connect-device",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <ConnectDevice />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
  helpFaq: {
    name: "help-faq",
    path: "/help/faq",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProtectedRoute>
          <FaqPage />
        </ProtectedRoute>
      </Suspense>
    ),
    hasHeader: false,
  },
};

/**
 * AppContent component that handles route rendering and cache warming
 * Separated to access router hooks like useLocation
 */
const AppContent = () => {
  const location = useLocation();

  // Warm up the cache for common data on initial application load
  useEffect(() => {
    // Load common data in the background
    warmCommonDataCache();

    // Mark navigation performance
    if (typeof performance !== "undefined") {
      performance.mark("app-initialized");
    }
  }, []);

  // Track route changes for performance monitoring
  useEffect(() => {
    if (typeof performance !== "undefined") {
      // Mark the start of route navigation
      performance.mark(`route-change-start-${location.pathname}`);

      // Measure the time it takes to complete the route change
      const completeNavigation = () => {
        performance.mark(`route-change-end-${location.pathname}`);
        performance.measure(
          `route-navigation-${location.pathname}`,
          `route-change-start-${location.pathname}`,
          `route-change-end-${location.pathname}`
        );

        // Log in development
        if (process.env.REACT_ENV === "development") {
          const measures = performance.getEntriesByName(
            `route-navigation-${location.pathname}`,
            "measure"
          );
          if (measures.length > 0) {
            console.log(
              `Navigation to ${location.pathname} took ${Math.round(
                measures[0].duration
              )}ms`
            );
          }
        }
      };

      // Schedule measurement after render cycle
      setTimeout(completeNavigation, 0);
    }

    // Optional: Prefetch data for next routes based on current location
    if (location.pathname === "/dashboard") {
      // This would be implemented when user context is available
      // Ex: if (accountId) warmServicesCache(accountId);
    }
  }, [location.pathname]);

  return (
    <Routes>
      {Object.values(routes).map(({ name, path, element, hasHeader }) => (
        <Route
          key={name}
          path={path}
          element={
            hasHeader ? (
              <>
                <Header />
                {element}
              </>
            ) : (
              element
            )
          }
        />
      ))}
    </Routes>
  );
};

/**
 * Main App component with providers and theming
 */
const App = () => {
  // Get the theme path at render time to ensure it's properly initialized
  // Use default as fallback if getThemePath() returns an invalid value
  const themePath = (getThemePath() || "default") as ThemePaths;

  // Use try-catch to handle any potential issues with theme generation
  let materialUITheme;
  try {
    materialUITheme = getMaterialUITheme(themePath);
  } catch (error) {
    console.error("Error creating theme, using default:", error);
    materialUITheme = getMaterialUITheme("default");
  }

  // Conditionally include PerformanceDashboard in development
  const PerformanceDashboard =
    process.env.REACT_ENV === "development"
      ? lazy(() => import("./components/PerformanceDashboard"))
      : null;

  return (
    <ThemeProvider theme={materialUITheme}>
      <GlobalStyles styles={globalStyle} />
      <ToastContainer />
      <AuthProvider>
        <AddressAndAccountContextProvider>
          <SubscriberPortalConfigurationContextProvider>
            <AppContent />
            {/* Show performance dashboard only in development */}
            {process.env.REACT_ENV === "development" &&
              PerformanceDashboard && (
                <Suspense fallback={null}>
                  <PerformanceDashboard />
                </Suspense>
              )}
          </SubscriberPortalConfigurationContextProvider>
        </AddressAndAccountContextProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
